import { useState, useEffect, useCallback } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  IconButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useDisclosure,
  useToast,
  Spinner,
  Flex,
  Heading,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Card,
  CardBody,
  useColorModeValue,
  useBreakpointValue,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
} from '@chakra-ui/react'
import {
  Plus,
  PencilSimple,
  Trash,
  Lightning,
  List,
} from '@phosphor-icons/react'
import api from '../services/api'
import socketService, { SOCKET_EVENTS } from '../services/socket'
import { useAuth } from '../contexts/AuthContext'
import { Sidebar } from '../layouts/DefaultLayout'
import ConfirmModal from '../components/ConfirmModal'

interface QuickReply {
  id: number
  command: string
  description: string
  message: string
  type: 'universal' | 'individual'
  agentId: number | null
  createdAt: string
  updatedAt: string
}

export default function QuickReplies() {
  const { canManageSystem } = useAuth()
  const toast = useToast()
  const isMobile = useBreakpointValue({ base: true, lg: false })
  const lightningSize = useBreakpointValue({ base: 24, md: 28 }) || 24
  const surfaceBg = useColorModeValue('white', 'gray.800')
  const subtleBg = useColorModeValue('gray.100', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const borderSubtle = useColorModeValue('gray.300', 'gray.600')
  const textPrimary = useColorModeValue('gray.800', 'white')
  const textSecondary = useColorModeValue('gray.600', 'gray.300')
  const textMuted = useColorModeValue('gray.500', 'gray.400')
  const menuIconColor = useColorModeValue('gray.700', 'white')

  const [universalReplies, setUniversalReplies] = useState<QuickReply[]>([])
  const [individualReplies, setIndividualReplies] = useState<QuickReply[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<QuickReply | null>(null)
  const [internalTicketsBadge, setInternalTicketsBadge] = useState(0)

  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isMenuOpen, onOpen: onMenuOpen, onClose: onMenuClose } = useDisclosure()

  const fetchInternalTicketsCount = async () => {
    try {
      const res = await api.get('/internal-tickets/my-pending-count')
      setInternalTicketsBadge(res.data.count || 0)
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    fetchInternalTicketsCount()
    const interval = setInterval(fetchInternalTicketsCount, 120000)

    const handleNew = () => fetchInternalTicketsCount()
    const handleReply = () => fetchInternalTicketsCount()
    const handleResolved = () => fetchInternalTicketsCount()

    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.NEW, handleNew)
    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.REPLY, handleReply)
    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.RESOLVED, handleResolved)

    return () => {
      clearInterval(interval)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.NEW, handleNew)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.REPLY, handleReply)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.RESOLVED, handleResolved)
    }
  }, [])
  const [editingReply, setEditingReply] = useState<QuickReply | null>(null)
  const [editingType, setEditingType] = useState<'universal' | 'individual'>('individual')
  const [form, setForm] = useState({ command: '', description: '', message: '' })

  const fetchReplies = useCallback(async () => {
    try {
      setLoading(true)
      const res = await api.get('/quick-replies')
      const all: QuickReply[] = res.data
      setUniversalReplies(all.filter((r) => r.type === 'universal'))
      setIndividualReplies(all.filter((r) => r.type === 'individual'))
    } catch (error) {
      toast({ title: 'Erro ao carregar respostas rápidas', status: 'error', duration: 3000 })
    } finally {
      setLoading(false)
    }
  }, [toast])

  useEffect(() => {
    fetchReplies()
  }, [fetchReplies])

  const openCreate = (type: 'universal' | 'individual') => {
    setEditingReply(null)
    setEditingType(type)
    setForm({ command: '', description: '', message: '' })
    onOpen()
  }

  const openEdit = (reply: QuickReply) => {
    setEditingReply(reply)
    setEditingType(reply.type)
    setForm({ command: reply.command, description: reply.description, message: reply.message })
    onOpen()
  }

  const handleSave = async () => {
    if (!form.command.trim() || !form.description.trim() || !form.message.trim()) {
      toast({ title: 'Preencha todos os campos', status: 'warning', duration: 3000 })
      return
    }

    setSaving(true)
    try {
      if (editingReply) {
        const url = editingType === 'universal'
          ? `/admin/quick-replies/${editingReply.id}`
          : `/quick-replies/${editingReply.id}`
        await api.put(url, form)
        toast({ title: 'Resposta rápida atualizada', status: 'success', duration: 3000 })
      } else {
        const url = editingType === 'universal' ? '/admin/quick-replies' : '/quick-replies'
        await api.post(url, form)
        toast({ title: 'Resposta rápida criada', status: 'success', duration: 3000 })
      }
      onClose()
      fetchReplies()
    } catch (error: any) {
      toast({
        title: 'Erro ao salvar',
        description: error?.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 3000,
      })
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = (reply: QuickReply) => {
    setDeleteTarget(reply)
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    try {
      const url = deleteTarget.type === 'universal'
        ? `/admin/quick-replies/${deleteTarget.id}`
        : `/quick-replies/${deleteTarget.id}`
      await api.delete(url)
      toast({ title: 'Resposta rápida excluída', status: 'success', duration: 3000 })
      fetchReplies()
    } catch {
      toast({ title: 'Erro ao excluir', status: 'error', duration: 3000 })
    } finally {
      setDeleteTarget(null)
    }
  }

  const ReplyTable = ({ replies, type }: { replies: QuickReply[]; type: 'universal' | 'individual' }) => {
    const canEdit = type === 'individual' || canManageSystem

    return (
      <Box overflowX="auto">
        <Table size="sm">
          <Thead>
            <Tr>
              <Th color={textMuted}>Comando</Th>
              <Th color={textMuted}>Descrição</Th>
              <Th color={textMuted}>Mensagem</Th>
              {canEdit && <Th color={textMuted} w="100px">Ações</Th>}
            </Tr>
          </Thead>
          <Tbody>
            {replies.length === 0 ? (
              <Tr>
                <Td colSpan={canEdit ? 4 : 3}>
                  <Text color={textMuted} textAlign="center" py={4}>
                    Nenhuma resposta rápida cadastrada
                  </Text>
                </Td>
              </Tr>
            ) : (
              replies.map((reply) => (
                <Tr key={reply.id}>
                  <Td>
                    <Badge colorScheme="yellow" fontSize="sm">/{reply.command}</Badge>
                  </Td>
                  <Td>
                    <Text color={textSecondary} fontSize="sm" noOfLines={2}>{reply.description}</Text>
                  </Td>
                  <Td>
                    <Text color={textMuted} fontSize="sm" noOfLines={2} maxW="400px">{reply.message}</Text>
                  </Td>
                  {canEdit && (
                    <Td>
                      <HStack spacing={1}>
                        <IconButton
                          aria-label="Editar"
                          icon={<PencilSimple size={16} />}
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          onClick={() => openEdit(reply)}
                        />
                        <IconButton
                          aria-label="Excluir"
                          icon={<Trash size={16} />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleDelete(reply)}
                        />
                      </HStack>
                    </Td>
                  )}
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>
    )
  }

  if (loading) {
    return (
      <Flex h="100%" align="center" justify="center">
        <Spinner size="lg" color="yellow.500" />
      </Flex>
    )
  }

  return (
    <>
      {/* Mobile menu drawer */}
      {isMobile && (
        <Drawer isOpen={isMenuOpen} placement="left" onClose={onMenuClose}>
          <DrawerOverlay />
          <DrawerContent bg={surfaceBg} maxW="280px">
            <DrawerCloseButton />
            <DrawerBody p={0}>
              <Sidebar onClose={onMenuClose} internalTicketsBadge={internalTicketsBadge} />
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}

      <Box h="100%" overflowY="auto" p={{ base: 3, md: 6 }}>
        <VStack align="stretch" spacing={{ base: 4, md: 6 }}>
          <HStack justify="space-between" spacing={3}>
            <HStack spacing={3} align="center" flex={1} minW={0}>
              {isMobile && (
                <IconButton
                  aria-label="Menu"
                  icon={<List size={20} />}
                  variant="ghost"
                  onClick={onMenuOpen}
                  size="sm"
                  color={menuIconColor}
                  _hover={{ bg: borderColor }}
                  flexShrink={0}
                />
              )}
              <Lightning size={lightningSize} weight="fill" color="#ECC94B" />
              <Heading size={{ base: 'md', md: 'lg' }} color={textPrimary}>Respostas Rápidas</Heading>
            </HStack>
          </HStack>

        <Tabs colorScheme="yellow" variant="enclosed">
          <TabList borderColor={borderColor}>
            <Tab color={textMuted} _selected={{ color: textPrimary, bg: subtleBg, borderColor: borderSubtle }}>
              Minhas Respostas
              <Badge ml={2} colorScheme="blue" borderRadius="full">{individualReplies.length}</Badge>
            </Tab>
            <Tab color={textMuted} _selected={{ color: textPrimary, bg: subtleBg, borderColor: borderSubtle }}>
              Universais
              <Badge ml={2} colorScheme="yellow" borderRadius="full">{universalReplies.length}</Badge>
            </Tab>
          </TabList>

          <TabPanels>
            <TabPanel px={0}>
              <Card bg={surfaceBg} borderColor={borderColor} borderWidth="1px">
                <CardBody>
                  <VStack align="stretch" spacing={4}>
                    <HStack justify="space-between">
                      <Text color={textSecondary} fontSize="sm">
                        Suas respostas rápidas pessoais. Somente você pode vê-las e usá-las.
                      </Text>
                      <Button
                        leftIcon={<Plus size={16} />}
                        colorScheme="yellow"
                        size="sm"
                        onClick={() => openCreate('individual')}
                      >
                        Nova Resposta
                      </Button>
                    </HStack>
                    <ReplyTable replies={individualReplies} type="individual" />
                  </VStack>
                </CardBody>
              </Card>
            </TabPanel>

            <TabPanel px={0}>
              <Card bg={surfaceBg} borderColor={borderColor} borderWidth="1px">
                <CardBody>
                  <VStack align="stretch" spacing={4}>
                    <HStack justify="space-between">
                      <Text color={textSecondary} fontSize="sm">
                        Respostas rápidas disponíveis para todos os atendentes.
                        {!canManageSystem && ' Somente administradores podem gerenciá-las.'}
                      </Text>
                      {canManageSystem && (
                        <Button
                          leftIcon={<Plus size={16} />}
                          colorScheme="yellow"
                          size="sm"
                          onClick={() => openCreate('universal')}
                        >
                          Nova Resposta
                        </Button>
                      )}
                    </HStack>
                    <ReplyTable replies={universalReplies} type="universal" />
                  </VStack>
                </CardBody>
              </Card>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent bg={surfaceBg} borderColor={borderColor} borderWidth="1px">
          <ModalHeader color={textPrimary}>
            {editingReply ? 'Editar' : 'Nova'} Resposta Rápida
            {editingType === 'universal' && (
              <Badge ml={2} colorScheme="yellow">Universal</Badge>
            )}
            {editingType === 'individual' && (
              <Badge ml={2} colorScheme="blue">Individual</Badge>
            )}
          </ModalHeader>
          <ModalCloseButton color={textMuted} />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel color={textSecondary}>Comando</FormLabel>
                <HStack>
                  <Text color={textMuted} fontSize="lg">/</Text>
                  <Input
                    placeholder="Ex: saudacao"
                    value={form.command}
                    onChange={(e) => setForm({ ...form, command: e.target.value.replace(/\s/g, '').toLowerCase() })}
                    bg={subtleBg}
                    borderColor={borderSubtle}
                    color={textPrimary}
                  />
                </HStack>
              </FormControl>

              <FormControl isRequired>
                <FormLabel color={textSecondary}>Descrição</FormLabel>
                <Input
                  placeholder="Ex: Saudação padrão ao cliente"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  bg={subtleBg}
                  borderColor={borderSubtle}
                  color={textPrimary}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel color={textSecondary}>Mensagem</FormLabel>
                <Textarea
                  placeholder="Ex: Olá! Seja bem-vindo ao suporte WeDrop. Como posso ajudá-lo?"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  bg={subtleBg}
                  borderColor={borderSubtle}
                  color={textPrimary}
                  rows={5}
                />
              </FormControl>

              {form.command && (
                <Box w="100%" p={3} bg={subtleBg} borderRadius="md">
                  <Text fontSize="xs" color={textMuted} mb={1}>Preview:</Text>
                  <Text fontSize="sm" color={textSecondary}>
                    Ao digitar <Badge colorScheme="yellow">/{form.command}</Badge> no chat, o texto será substituído por:
                  </Text>
                  <Text fontSize="sm" color={textPrimary} mt={2} whiteSpace="pre-wrap">{form.message || '...'}</Text>
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose} color={textMuted}>
              Cancelar
            </Button>
            <Button colorScheme="yellow" onClick={handleSave} isLoading={saving}>
              {editingReply ? 'Salvar' : 'Criar'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Excluir Resposta Rápida"
        message={deleteTarget ? `Excluir a resposta rápida "/${deleteTarget.command}"?` : ''}
        confirmLabel="Excluir"
      />
    </Box>
    </>
  )
}
